import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import styled from 'styled-components';
import { animated } from 'react-spring';
import { useMutation } from '@apollo/client';

import { SiteContext } from '../../../../utils/context/SiteContext';
import { useForm } from '../../../../utils/hooks/useForm';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';

import { Section } from '../../../layout/Section';
import { Container } from '../../../layout/Container';
import { Thanks } from '../../../layout/Thanks';

import { Input } from '../../../ui/form/Input';
import { FormError } from '../../../ui/form/FormError';
import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';
import { Form, FormRow } from '../../../ui/form/Form';
import { ButtonGroup } from '../../../ui/button/ButtonGroup';
import { Button } from '../../../ui/button/Button';
import { ButtonLink } from '../../../ui/link/ButtonLink';
import { useFadeTransition } from '../../../../utils/hooks/useFadeTransition';
import { Seo } from '../../../../utils/seo/Index';

import { Hero } from '../../../ui/hero/Index';
import { GENERAL_VALUATION_MUTATION } from '../../../../../client/__graphql__/mutations/valuation';
import { createGTMEvent } from '../../../../utils/tracking/gtm';
import {
  LeadType,
  PageType
} from '../../../../__types__/generated/globalTypes';
import { useDigtectiveContext } from '../../../../__lib__/digtective/context/hooks/useDigtectiveContext';

interface IRoute {
  office: string;
  path: string;
}

const FritidsArtikkel2: React.FC<RouteComponentProps<IRoute>> = ({
  location,
  match
}) => {
  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });

  return (
    <>
      <Seo
        title="Fremhever kvalitetene som oppfyller drømmen om hytte- PrivatMegleren"
        description="For de som vil realisere hyttedrømmen er mulighetene for både
        aktiviteter og rekreasjon vel så viktig som selve hytta. Det er
        derfor viktig med et grundig forarbeid for at fritidseiendommen skal
        appellere til flest mulig."
        url={location.pathname}
      />
      <Hero
        imageUrl="https://cdn.reeltime.no/pm_assets/kampanjer/leveranse/fritid/Toppbilde_2.jpg"
        opacity={0.4}
        className="content-height flex padding-top-50"
        alt="bilde"
      >
        <HeroContent style={pageFadeIn}>
          <Heading tag="h1" center={true}>
            Fremhever kvalitetene som oppfyller drømmen om hytte
          </Heading>
          <Paragraph center={true}>
            For de som vil realisere hyttedrømmen er mulighetene for både
            aktiviteter og rekreasjon vel så viktig som selve hytta. Det er
            derfor viktig med et grundig forarbeid for at fritidseiendommen skal
            appellere til flest mulig.
          </Paragraph>
        </HeroContent>
      </Hero>
      <Container style={pageFadeIn}>
        <Section>
          <Paragraph center={true}>
            De erfarne eiendomsmeglerne Pia M. Murland og Gustav J. Sædberg
            forteller at fritidseiendom står sterkt i befolkningen. Samtidig
            understreker de at fritidseiendom ikke er et grunnleggende behov på
            samme måte som en eiendom – det er rett og slett ikke noe man må ha.
            Derfor er det viktig å skape den riktige følelsen hos kjøperen, og
            fremheve hvordan fritidseiendommen du selger kan oppfylle drømmen.
          </Paragraph>
          <Heading tag="h3" center={true}>
            Legger et godt grunnlag på befaringen
          </Heading>
          <Paragraph center={true}>
            Meglerne opplever at de fleste hytteeiere er ute i god tid før de
            skal selge. Det er en fordel, fordi det gir bedre tid til å sørge
            for at alt er på stell etter meglers befaring. Hvordan befaringen
            gjennomføres avhenger ofte av beliggenheten.
          </Paragraph>
          <Paragraph center={true}>
            – Mange befaringer på fjellet foretas uten at selger er til stede,
            særlig om selger har lang reisevei. Rent praktisk får megler da
            tilgang til nøkkel, og har kontakt med selger på telefon om det er
            behov, sier Pia M. Murland i PrivatMegleren Lillehammer.
          </Paragraph>
          <Paragraph center={true}>
            Hennes kollega Gustav forteller at det som regel er motsatt for
            hytter ved sjøen.
          </Paragraph>
          <Paragraph center={true}>
            – Om hytta ligger i skjærgården er selger ofte med på befaringen,
            fordi man i større grad er avhengig av båt for å komme seg ut til
            eiendommen. Det er også nyttig at selger kan fortelle om det nære
            ved hytta og hvordan de har brukt eiendommen, forteller
            eiendomsmegler Gustav J. Sædberg i PrivatMegleren Sædberg & Lian.
          </Paragraph>
          <Paragraph center={true}>
            Basert på funnene fra befaringen vil megleren gi deg anbefaling om
            hvilke grep man kan ta for å vise eiendommen fra sin beste side. De
            erfarne meglerne er samstemte om at det vanligvis er god nok tid til
            å prioritere utbedringer og sørge for å få alle detaljene på plass,
            ettersom mange selgerne planlegger godt for salget.
          </Paragraph>

          <Paragraph center={true}>
            – Godt vedlikehold er et kvalitetstegn for kjøperne, og får de ikke
            den riktige følelsen kan de nærmest snu i døra. Selv små grep kan
            gjøre at hytta appellerer til flere. Om man får med seg en ekstra
            interessent inn i budrunden har det mye å si for den endelige
            prisen, forklarer Pia.
          </Paragraph>
          <Paragraph center={true}>
            – Detaljene handler også om mer enn det visuelle. Det kan for
            eksempel finnes muntlige avtaler om slikt som parkeringsplass og
            båtplass, som bør formaliseres og nedtegnes skriftlig før salget. Da
            er det greit å ha god tid til papirarbeidet, utdyper Gustav.
          </Paragraph>
          <ImageContainer>
            <img
              style={{ width: '100%' }}
              src="https://cdn.reeltime.no/pm_assets/kampanjer/leveranse/fritid/Artikkelbilde_2.jpg"
              alt="artikkel bilde"
              className="artikkelbilde_1"
            />
          </ImageContainer>
          <Heading tag="h3" center={true}>
            Fremhever bruken
          </Heading>
          <Paragraph center={true}>
            I presentasjonen av fritidseiendommen jobber meglerne aktivt for å
            gjenspeile hyttedrømmen. Også her finnes det forskjeller mellom
            hytter ved sjøen og på fjellet. Gustav trekker frem som eksempel at
            mange har et ønske om å være aktiv på hytter ved sjøen.
          </Paragraph>
          <Paragraph center={true}>
            – Vi tilrettelegger slik at man får lyst til å bruke eiendommen med
            en gang, gjennom å vise frem bruken. Mange vil være aktive sammen
            med barna. Det er her barna lærer seg å svømme, fange krabber fra
            bryggekanten og drar på fisketurer sammen med de voksne. Da tar det
            seg bra ut vi har rigget båten klar ved bryggekanten, og
            redningsvestene henger klare i sjøboden, forklarer han.
          </Paragraph>
          <Paragraph center={true}>
            Også på fjellet er det mange aktiviteter for store og små utenfor
            hyttedøra. Pia forteller imidlertid at potensielle kjøpere her
            oftest ser etter et rolig sted, med en varm, lun og koselig stemning
            innendørs.
          </Paragraph>
          <Paragraph center={true}>
            – Det handler om å gi følelsen av at man kan trives der, bare fra å
            se på bildene. Hvert rom forberedes derfor godt, slik at ingenting
            er overlatt til tilfeldighetene. Sengene er redd opp med sengeteppe,
            det er rent, ryddig og lys i vinduene. Pleddet ligger klar foran
            peisen, og alt er klargjort for ro og avslapning, forteller Pia.
          </Paragraph>
          <Paragraph center={true}>
            Når hytta er klargjort for å vises frem fra sin beste side fanger
            PrivatMeglerens håndplukkede fotografer den gode stemningen på foto
            til bruk i annonseringen – også fra fugleperspektiv.
          </Paragraph>
          <Paragraph center={true}>
            – Vi bruker ofte dronebilder for å gi riktig inntrykk av
            beliggenheten, størrelsen på eiendommen og avstand til ting i
            området. Et utvalg av informative symboler brukes for å merke av
            steder og aktiviteter i området, og gjør det lettere for
            interessenter å plassere hytta i nærområdet, sier Pia.
          </Paragraph>
          <Heading tag="h3" center={true}>
            Visning med hvilepuls
          </Heading>
          <Paragraph center={true}>
            Når eiendommen er gjort klar for å presenteres for markedet er det
            duket for visning. Meglerne forteller at det er en høyere andel
            privatvisninger av hytter enn ved salg &nbsp; av eiendom.
          </Paragraph>
          <Paragraph center={true}>
            – Folk er mye mer avslappet på hyttevisninger, og stiller gjerne i
            fritidsklær. Ofte kombinerer folk visningen med en utflukt i
            nærheten, slik at de kan bli bedre kjent i området. Det er ikke
            uvanlig at de som kommer på visning ikke har vært på stedet før. Da
            er det en fordel om megler har høy kunnskap om alt som rører seg i{' '}
            <a href="https://privatmegleren.no/kampanje/fritidsartikkel">
              nærmiljøet
            </a>
            , forklarer Pia.
          </Paragraph>
          <Paragraph center={true}>
            – Vi opplever en del flere andregangsvisninger av fritidseiendommer,
            hvor man kanskje også har med seg håndverker. I skjærgården må
            fellesvisning av hytter planlegges godt, blant annet fordi man må
            sørge for noen som kjører båt frem og tilbake, legger Gustav til.
          </Paragraph>
          <Paragraph center={true}>
            Selv om det før visning er viktig å gjøre hytta nøytral for flest
            mulig vil små grep kunne forsterke idyllen kjøperne ser for seg.
          </Paragraph>
          <Paragraph center={true}>
            – Det er gjerne de små tingene som teller. At skiene står utenfor og
            fyr i peisen, bidrar til den lune stemningen. I påsketiden er det
            gjerne pyntet med påskeliljer, appelsiner og nytraktet kaffe.
            Stemningen når man da kommer ut på verandaen i solskinnet er
            ubetalelig, beskriver Pia.
          </Paragraph>
        </Section>
        <SelgeForm />
        {/*  <KjopeForm /> */}
      </Container>
    </>
  );
};

const FormWrapper = styled(animated.div)``;
const SelgeForm: React.FC = () => {
  const { routeConfig }: any = React.useContext(SiteContext);
  const [posted, setPosted] = React.useState(false);
  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const { submitWithDigger } = useDigtectiveContext();

  const [lead, { error, loading }] = useMutation(GENERAL_VALUATION_MUTATION, {
    onCompleted: ({ lead }) => {
      if (lead.valuation?.success) {
        setPosted(true);

        // GTM tracking

        createGTMEvent({
          event: 'pmFormSubmission',
          gaEvent: 'RT_VALUATION',
          gaCategory: 'RT_VALUATION',
          gaAction: 'RT_SEND_VALUATION'
        });
      }
    }
  });

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'name',
        placeholder: 'Eks. Ola Nordmann',
        value: '',
        label: 'Navn *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Postnummer *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);
      submitWithDigger({ zip: preparedFields.zip }, ({ diggerId }) => {
        lead({
          variables: {
            input: {
              ...preparedFields,
              diggerId,
              referer: `${location.pathname}${location.search}`,
              leadType: LeadType.VERDIVURDERING,
              pageType: PageType.GENERELL
            }
          }
        });
      });
    }
  });

  return (
    <>
      <FormWrapper style={fadeOut}>
        <Section className="form-section" style={{ paddingBottom: '10px' }}>
          <Heading tag="h1" center={true}>
            Bli kontaktet
          </Heading>
          <Paragraph center={true} className="form-text">
            Ønsker du å vite mer om hvordan vi kan &nbsp; hjelpe deg?
          </Paragraph>
          <Paragraph center={true} className="form-text">
            Fyll ut skjemaet og en av våre meglere i ditt nærområde kontakt.
          </Paragraph>
        </Section>
        <Form
          onSubmit={handleSubmit}
          noValidate
          style={{ paddingBottom: '50px' }}
        >
          {error &&
            error.graphQLErrors.map((error, index) => (
              <FormError key={`error_${index}`} message={error.message} />
            ))}
          {fields.map((item, index) => {
            return (
              <FormRow key={`field_${index}`}>
                <Input
                  type={item?.type}
                  name={item?.name}
                  label={item?.label}
                  labelAfter={item?.labelAfter}
                  placeholder={item?.placeholder}
                  value={item?.value}
                  onChange={handleChange}
                  error={item?.error}
                />
              </FormRow>
            );
          })}
          <ButtonGroup>
            <Button
              type="primary"
              colorTheme="gold"
              disabled={loading}
              loading={loading}
            >
              Kontakt meg
            </Button>
            <ButtonLink
              as="a"
              href={routeConfig.personvern.url}
              className="privacy-link-mobile"
            >
              Personvernpolicy
            </ButtonLink>
          </ButtonGroup>
        </Form>
      </FormWrapper>
      <Thanks style={fadeIn}>
        <Section className="form-section">
          <Heading tag="h1" center={true}>
            Tusen takk
          </Heading>
          <Paragraph center={true}>
            En eiendomsmegler vil ta kontakt med deg så snart som mulig.
          </Paragraph>
        </Section>
      </Thanks>
    </>
  );
};

export default FritidsArtikkel2;

const HeroContent = styled(animated.div)`
  display: flex;
  flex-flow: column;
  align-items: center;
  width: 100%;
  max-width: 960px;
  padding: 0 30px;

  h1 {
    margin-bottom: 0;
    width: 100%;
    word-break: break-word;
  }

  h2 {
    width: 100%;
    word-break: break-word;
  }
`;

const ImageContainer = styled.div`
  img  {
    object-fit: contain;
    display: block;
    margin-left: auto;
    margin-right: auto;
    width: 250px;
  }

  @media all and (min-width: 600px) {
    img {
      object-fit: contain;
      display: block;
      margin-left: auto;
      margin-right: auto;
      width: 50%;
      padding: 1em 0 1em;
    }
  }
`;

/* const StyledTop = styled(animated.div)`
  margin-top: 66px;
  width: 100%;
  overflow: hidden;
  margin-bottom: 3em;

  img {
    width: 100%;
    max-height: 500px;
    object-fit: contain;
    object-position: center;
  }

  .video-mobil {
    display: block;
  }
  .video-desktop {
    display: none;
  }

  @media (min-width: 600px) {
    .video-mobil {
      display: none;
    }
    .video-desktop {
      display: block;
    }
  }

  @media all and (max-width: 1280px) {
    img {
      min-height: 300px;
      object-fit: cover;
    }
  }
`; */
